import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { panelPath } from '../../config/paths';
import { useAuth } from '../../context/AuthContext';
import type { AuthUser } from '../../api/client';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 8; // ~12s total — bridges the Lemon Squeezy webhook delay

function planSnapshot(u: AuthUser | null): string {
  return `${u?.plan ?? ''}|${u?.subscriptionStatus ?? ''}|${u?.canUseAI ?? ''}|${u?.hasProSubscription ?? ''}|${u?.aiCredits?.balance ?? ''}`;
}

export default function CheckoutSuccessPage() {
  const { t } = useTranslation();
  const { user, refetchUser } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const [syncing, setSyncing] = useState(true);
  const [timedOut, setTimedOut] = useState(false);

  // Lemon Squeezy confirms payment via an async webhook, so the plan/credits the API
  // returns right after redirect back here can still be pre-purchase. Poll until they
  // change (or we give up) instead of showing a possibly-stale "Pro is active" message.
  useEffect(() => {
    const initialSnapshot = planSnapshot(userRef.current);
    let cancelled = false;

    (async () => {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
        await refetchUser();
        if (cancelled) return;
        if (planSnapshot(userRef.current) !== initialSnapshot) {
          setSyncing(false);
          return;
        }
        if (attempt < MAX_POLL_ATTEMPTS - 1) {
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
          if (cancelled) return;
        }
      }
      if (!cancelled) {
        setSyncing(false);
        setTimedOut(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-md mx-auto text-center">
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 shadow-sm">
        {syncing ? (
          <Loader2 className="h-16 w-16 text-primary-500 dark:text-primary-400 mx-auto mb-4 animate-spin" />
        ) : (
          <CheckCircle className="h-16 w-16 text-green-500 dark:text-green-400 mx-auto mb-4" />
        )}
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {syncing ? t('checkoutSuccess.syncingTitle') : t('checkoutSuccess.title')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {syncing
            ? t('checkoutSuccess.syncingSubtitle')
            : timedOut
              ? t('checkoutSuccess.delayedSubtitle')
              : t('checkoutSuccess.subtitle')}
        </p>
        <Link
          to={panelPath('')}
          className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
        >
          {t('checkoutSuccess.cta')}
        </Link>
      </div>
    </div>
  );
}
